import { METHODS, num, fmtFull } from "./format";

const ESC = 0x1b;
const GS = 0x1d;

export class EscPos {
  constructor(cols = 32) {
    this.cols = cols;
    this.buf = [];
    this.raw([ESC, 0x40]);
  }
  raw(arr) {
    for (const b of arr) this.buf.push(b);
    return this;
  }
  text(s) {
    const clean = String(s).normalize("NFD").replace(/[^\x20-\x7E\n]/g, "");
    for (const c of clean) this.buf.push(c.charCodeAt(0));
    return this;
  }
  line(s = "") {
    return this.text(s + "\n");
  }
  wrap(s) {
    const words = String(s).split(" ");
    let cur = "";
    for (const w of words) {
      if ((cur + " " + w).trim().length > this.cols) {
        this.line(cur);
        cur = w;
      } else cur = (cur + " " + w).trim();
    }
    if (cur) this.line(cur);
    return this;
  }
  align(a) {
    return this.raw([ESC, 0x61, { left: 0, center: 1, right: 2 }[a]]);
  }
  bold(on) {
    return this.raw([ESC, 0x45, on ? 1 : 0]);
  }
  size(w, h) {
    return this.raw([GS, 0x21, ((w - 1) << 4) | (h - 1)]);
  }
  sep(ch = "-") {
    return this.line(ch.repeat(this.cols));
  }
  pair(l, r) {
    l = String(l);
    r = String(r);
    const space = this.cols - l.length - r.length;
    if (space < 1) return this.line(l).line(r.padStart(this.cols));
    return this.line(l + " ".repeat(space) + r);
  }
  feed(n = 3) {
    return this.raw([ESC, 0x64, n]);
  }
  cut() {
    return this.raw([GS, 0x56, 0x42, 0x00]);
  }
  drawer(pin = 0) {
    for (const m of pin === 2 ? [0, 1] : [pin]) this.raw([ESC, 0x70, m, 0x3c, 0xf0]);
    return this;
  }
  barcode(data, { height = 70, width = 2 } = {}) {
    const d = "{B" + String(data).replace(/[^\x20-\x7E]/g, "");
    this.raw([GS, 0x68, height, GS, 0x77, width, GS, 0x48, 2, GS, 0x66, 0]);
    this.raw([GS, 0x6b, 73, d.length]);
    return this.text(d);
  }
  bytes() {
    return new Uint8Array(this.buf);
  }
}

export const colsFor = (width) => (width === "80mm" ? 48 : 32);

export function buildReceipt(tx, store, { width = "58mm", openDrawer = false, pin = 0, cut = true, logo = null } = {}) {
  const p = new EscPos(colsFor(width));
  if (openDrawer) p.drawer(pin);
  if (logo) p.align("center").raw(logo).line("");
  p.align("center").bold(true).size(1, 2).line(store.store_name).size(1, 1).bold(false);
  if (store.tagline) p.wrap(store.tagline);
  if (store.address) p.wrap(store.address);
  if (store.phone) p.line("Telp: " + store.phone);
  p.align("left").sep();
  p.line("No   : " + tx.invoice);
  p.line("Tgl  : " + fmtFull(tx.created_at));
  p.line("Kasir: " + tx.cashier_name);
  if (tx.customer_name) p.line("Plgn : " + tx.customer_name);
  p.sep();
  for (const it of tx.items) {
    p.wrap(it.name);
    p.pair(`  ${it.qty} x ${num(it.price)}`, num(it.price * it.qty));
    if (it.discount) p.pair("  " + (it.promo_name || "Diskon").slice(0, p.cols - 12), "-" + num(it.discount));
  }
  p.sep();
  p.pair("Subtotal", num(tx.subtotal));
  if (tx.discount) p.pair("Diskon", "-" + num(tx.discount));
  if (tx.points_discount) p.pair(`Tukar ${tx.points_redeemed} poin`, "-" + num(tx.points_discount));
  if (tx.tax) p.pair(`Pajak (${tx.tax_percent}%)`, num(tx.tax));
  p.bold(true).pair("TOTAL", "Rp " + num(tx.total)).bold(false);
  p.pair("Bayar (" + (METHODS[tx.payment_method] || tx.payment_method) + ")", num(tx.paid));
  if (tx.payment_method === "tunai") p.pair("Kembali", num(tx.change));
  if (tx.customer_id) {
    p.sep().pair("Member", tx.customer_name.slice(0, p.cols - 8));
    if (tx.points_earned) p.pair("Poin didapat", "+" + tx.points_earned);
    if (tx.customer_points != null) p.pair("Total poin", num(tx.customer_points));
  }
  if (tx.status === "batal") p.align("center").bold(true).line("*** DIBATALKAN ***").bold(false).align("left");
  p.sep();
  p.align("center");
  if (store.footer) p.wrap(store.footer);
  p.line("");
  p.feed(3);
  if (cut) p.cut();
  return p.bytes();
}

export function buildLabels(labels, store, { width = "58mm", showName = true, showPrice = true, showStore = false } = {}) {
  const p = new EscPos(colsFor(width));
  labels.forEach((l, idx) => {
    p.align("center");
    if (showStore) p.line(store?.store_name || "");
    if (showName) p.bold(true).wrap(l.name).bold(false);
    if (l.barcode) p.barcode(l.barcode, { width: width === "80mm" ? 3 : 2 }).line("");
    if (showPrice) p.bold(true).size(1, 2).line("Rp " + num(l.price)).size(1, 1).bold(false);
    p.feed(1);
    if (idx < labels.length - 1) p.line("- ".repeat(Math.floor(colsFor(width) / 2)));
  });
  p.feed(3).cut();
  return p.bytes();
}

export function buildShiftReport(s, store, width = "58mm") {
  const p = new EscPos(colsFor(width));
  p.align("center").bold(true).line(store?.store_name || "").line("LAPORAN TUTUP KASIR").bold(false).align("left").sep();
  p.line("Kasir : " + s.cashier_name);
  p.line("Buka  : " + fmtFull(s.opened_at));
  p.line("Tutup : " + (s.closed_at ? fmtFull(s.closed_at) : "-"));
  p.sep();
  p.pair("Jumlah transaksi", String(s.tx_count));
  p.pair("Item terjual", String(s.items_sold));
  if (s.void_count) p.pair("Dibatalkan", String(s.void_count));
  p.pair("Total diskon", num(s.discount_total));
  p.sep();
  for (const m of s.by_method || []) p.pair(METHODS[m.method] || m.method, num(m.total));
  p.bold(true).pair("TOTAL PENJUALAN", num(s.total_sales)).bold(false);
  p.sep();
  p.pair("Kas awal", num(s.opening_cash));
  p.pair("Penjualan tunai", num(s.cash_sales));
  p.pair("Kas seharusnya", num(s.expected_cash));
  if (s.closing_cash != null) {
    p.pair("Kas dihitung", num(s.closing_cash));
    p.bold(true).pair("Selisih", (s.difference > 0 ? "+" : "") + num(s.difference)).bold(false);
  }
  if (s.closing_note) p.sep().wrap("Catatan: " + s.closing_note);
  p.sep().line("").line("Ttd Kasir        Ttd Supervisor").line("").line("");
  p.feed(3).cut();
  return p.bytes();
}

export function buildTestPage(store, width) {
  const p = new EscPos(colsFor(width));
  p.align("center").bold(true).size(2, 2).line("TES PRINT").size(1, 1).bold(false);
  p.line(store?.store_name || "PosKasir Pro");
  p.sep();
  p.align("left").line("Lebar kertas : " + width);
  p.line("Karakter/baris: " + colsFor(width));
  p.pair("Kiri", "Kanan");
  p.line("0123456789".repeat(5).slice(0, colsFor(width)));
  p.sep();
  p.align("center").line("Printer siap digunakan!");
  p.feed(3).cut();
  return p.bytes();
}
